import { useCallback, useEffect, useRef, useState } from 'react';
import { apiClient, useAuth } from '../../../auth-lib';
import {
  Button, Input, Select, StatusPill, useToast,
} from '../../../design-system';
import '../../styles/TallyPanel.css';

// Can this machine's Tally open a company by itself?
//
// ⚠️ THE ANSWER DECIDES WHETHER UNATTENDED SYNC IS POSSIBLE AT ALL, and it was
// computed and shown to nobody. `describeOpenStrategy` has been hanging off
// every connection since detection was built, and no screen read it — so a firm
// on the 'none' verdict watched clients silently never refresh, with a
// connection reporting active and a sync reporting success, because skipping a
// company that is closed is not an error.
//
// ⚠️ AND IT COULD ONLY BE MEASURED BY A SYNC. Detection is queued inside
// `planSync`, so a firm whose sync has never run has never been asked the
// question — and one that has just moved from an educational Tally to a
// licensed one keeps the old, stricter verdict until a sync happens to
// re-measure. Educational builds restrict the gateway in ways a Gold licence
// does not, so that stale verdict is exactly the case worth re-testing.

const MANAGER_LEVEL = 3;
const POLL_MS = 2000;
const POLL_LIMIT = 60;

const TONE = { info: 'success', warning: 'warning' };

function TallyOpenStrategy({ agentId, companies = [] }) {
  const toast = useToast();
  const { user } = useAuth();
  const [verdict, setVerdict] = useState(null);
  const [company, setCompany] = useState('');
  const [testing, setTesting] = useState(false);
  const [showEvidence, setShowEvidence] = useState(false);
  const timer = useRef(null);

  const canTest = (user?.role?.level || 0) >= MANAGER_LEVEL;

  const load = useCallback(async () => {
    if (!agentId) { setVerdict(null); return; }
    try {
      const { data } = await apiClient.get(`/api/tally/machines/${agentId}/open-strategy`);
      setVerdict(data);
    } catch {
      setVerdict(null);
    }
  }, [agentId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => clearTimeout(timer.current), []);

  // ⚠️ DELIBERATELY NOT PREFILLED FROM THE SCAN ANY MORE. It used to default to
  // companies[0], which on this gateway is always a company that is already
  // OPEN — so the one-click path ran the test against the one target that can
  // never answer the question. Leaving it blank makes naming a closed company a
  // deliberate act, which is what the measurement requires.

  const watch = useCallback((jobId, tries = 0) => {
    timer.current = setTimeout(async () => {
      try {
        const { data } = await apiClient.get(`/api/tally/jobs/${jobId}`);
        if (data.status === 'succeeded') { await load(); setTesting(false); return; }
        if (data.status === 'failed' || data.status === 'expired') {
          toast.danger(data.error || 'The machine could not run the test');
          setTesting(false);
          return;
        }
        if (tries >= POLL_LIMIT) {
          toast.danger('The machine did not answer. Is the agent running with --tally-sync?');
          setTesting(false);
          return;
        }
        watch(jobId, tries + 1);
      } catch {
        watch(jobId, tries + 1);
      }
    }, POLL_MS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  const test = async () => {
    if (!company) return;
    // ⚠️ SAID BEFORE IT HAPPENS, NOT AFTER. The probes close the target company
    // on purpose — a candidate "tested" against one that was already open proves
    // nothing — and somebody may be sitting at that PC working in it.
    // eslint-disable-next-line no-alert
    const ok = window.confirm(
      `This will close "${company}" in Tally on that computer, try several ways of `
      + 'reopening it, and report which worked.\n\nDo not run this while somebody is '
      + 'working in that company.',
    );
    if (!ok) return;

    setTesting(true);
    try {
      const { data } = await apiClient.post(
        `/api/tally/machines/${agentId}/detect-open`,
        { company },
      );
      watch(data.jobId);
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not start the test');
      setTesting(false);
    }
  };

  if (!agentId) return null;

  const measured = verdict && verdict.value !== 'unknown';

  return (
    <div className="tally-strategy">
      <div className="tally-status-row">
        <strong>Opening companies by itself:</strong>
        {measured ? (
          <StatusPill
            variant={TONE[verdict.tone] || 'warning'}
            label={verdict.syncsOnlyOpenCompanies ? 'Cannot — open companies only' : 'Yes'}
          />
        ) : (
          <StatusPill variant="default" label="Not measured yet" />
        )}
      </div>

      {verdict && (
        <p className={verdict.syncsOnlyOpenCompanies ? 'tally-warn' : 'tally-muted'}>
          <strong>{verdict.headline}</strong> {verdict.detail}
        </p>
      )}

      {measured && verdict.detectedAt && (
        <p className="tally-muted">
          Measured {new Date(verdict.detectedAt).toLocaleString()}.
          {' '}
          {/* ⚠️ THE ONE SENTENCE THAT MATTERS FOR A FIRM THAT HAS UPGRADED. A
              verdict taken on an educational build is not a fact about a
              licensed one, and nothing re-measures on its own. */}
          If you have changed your Tally licence or version since, test again —
          this answer is about the build that was running then.
        </p>
      )}

      <div className="tally-actions">
        {/* ⚠️ THE TARGET MUST BE TYPEABLE, and until now it was not — it came
            only from the scan. That is circular on exactly the machine where the
            answer matters: the scan can only ever return the company that is
            already OPEN (Tally's gateway ignores the data folder — see
            `listCompanies`), and testing "can Tally open this?" against a
            company that is already open proves nothing at all. The whole point
            is to name one that is CLOSED, which is precisely the name the scan
            cannot give you. Typed exactly as Tally's Select Company screen
            spells it. */}
        {companies.length > 0 && (
          <Select
            aria-label="Company to test with"
            value={companies.includes(company) ? company : ''}
            onChange={(e) => setCompany(e.target.value)}
            disabled={testing}
          >
            <option value="">Type a company name…</option>
            {companies.map((c) => <option key={c} value={c}>{c}</option>)}
          </Select>
        )}
        <Input
          aria-label="Company to test with"
          placeholder="Company name, exactly as Tally shows it"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          disabled={testing}
        />
        {canTest ? (
          <Button
            variant="secondary"
            onClick={test}
            loading={testing}
            disabled={testing || !company}
          >
            {measured ? 'Test again' : 'Test this Tally'}
          </Button>
        ) : (
          <small className="tally-muted">A manager can run this test.</small>
        )}
      </div>

      {/* The firm's own machine and their own evidence — "what did it actually
          try?" is the first question anyone asks about a verdict they dislike. */}
      {verdict?.evidence && (
        <>
          <button
            type="button"
            className="tally-linkish tally-linkish-inline"
            onClick={() => setShowEvidence((v) => !v)}
          >
            {showEvidence ? 'Hide what it tried' : 'What did it try?'}
          </button>
          {showEvidence && <pre className="tally-evidence">{verdict.evidence}</pre>}
        </>
      )}
    </div>
  );
}

export default TallyOpenStrategy;
